import React, { useCallback, useEffect, useState } from "react";
import { store } from "../../store";
import { observer } from "mobx-react";
import { FusionBody } from "@/components/ui/fusion-body";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import elofleetLogoUrl from "../../images/elofleet-logo.svg";
import { consumeLogoutReason, LoginStatus, LogoutReason } from "../../domain/services/service-auth";
import { Navigate, NavLink, useLocation } from "react-router";
import { routePath } from "../../route-path";
import { useFetchAction } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { useFetchPublicSettings } from "@/lib/hooks/public-setting";
import { ResetPasswordModal } from "@/parts/reset-password";
import { ShowcasePanel } from "@/parts/showcase";
import { PasswordInput } from "@/components/ui/password-input";
import { useForm } from "react-hook-form";
import { ErrorText } from "@/components/ui/error-text";
import { rfc5322Email } from "@/lib/validation";
import { LanguageSelector } from "@/parts/language-selector";
import { Brands } from "@/parts/brands";
import { Mode } from "../../../src/api";
import { LoginBackground } from "@/parts/login-background";
import { Footer } from "@/parts/footer";
import { NotACustomerYet } from "@/parts/not-a-customer-yet";
import { bookDemoUrl } from "../../utils/constants";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

/**
 * @returns the latest path the user wanted to visit before being redirected to the login page. If there is none it returns the dashboard path.
 */
const useTargetPath = (mode?: Mode): string => {
    const { state } = useLocation();
    return typeof state === "object" &&
        state !== null &&
        Object.hasOwnProperty.apply(state, ["from"])
        ? ((state as Record<string, unknown>)["from"] as Location).pathname
        : mode === Mode.ELOSHIELD
          ? routePath.actionConfiguration()
          : routePath.dashboard();
};

type Form = {
    email: string;
    password: string;
};

export const PageLogin = observer((): JSX.Element => {
    const t = store.i18n.t;
    const [loginError, setLoginError] = useState<boolean>(false);
    const [loginLoading, setLoginLoading] = useState(false);
    const [resetPasswordModalOpened, setResetPasswordModalOpened] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<Form>();

    const [publicSettings] = useFetchPublicSettings();
    const targetPath = useTargetPath(publicSettings?.mode);

    // Get SSO authentication URL if the backend has a configured OIDC client in place.
    const getSsoAuthUrl = useCallback(() => {
        if (publicSettings?.openidConnectConfigured) {
            return store.loginTokensApi.loginTokensAuthRoute({ redirect: targetPath });
        }
        return Promise.resolve(undefined);
    }, [publicSettings, targetPath]);
    const [ssoAuthUrl] = useFetchAction(getSsoAuthUrl);

    // Checks whether a login token is provided in the URL. This is the case after a successful authentication via SSO.
    useEffect(() => {
        const fragment = store.historyProvider.getFragment("loginToken") as string | undefined;
        if (fragment === undefined) {
            return;
        }
        store.serviceLoginToken.setLoginToken({ ...JSON.parse(fragment), context: "sso" });
    }, []);

    useEffect(() => {
        const logoutReason = consumeLogoutReason();
        if (logoutReason != null) {
            if (logoutReason === LogoutReason.USER_LOGOUT) {
                toast.success(t(`page.login.logoutReason.${logoutReason}`));
            } else {
                toast.warning(t(`page.login.logoutReason.${logoutReason}`));
            }
        }
    }, [t]);

    const ssoError = store.historyProvider.getQueryParam("ssoError") as string | undefined;

    if (store.serviceAuth.isLoggedIn) {
        return <Navigate to={targetPath} />;
    }

    // If there is a redirect string in current URL navigate to it. This should only be the case
    // on a successful SSO login.
    const redirect = store.historyProvider.getQueryParam("redirect") as string | undefined;
    if (redirect) {
        return <Navigate to={redirect} />;
    }

    const handleLoginWithPassword = async (form: Form): Promise<void> => {
        setLoginLoading(true);
        try {
            const result = await store.serviceAuth.login({
                email: form.email,
                password: form.password,
            });
            if (result.status === LoginStatus.INVALID) {
                setLoginError(true);
            } else {
                setLoginError(false);
                store.serviceErrors.dismissAllErrors();
            }
        } catch (e) {
            setLoginError(true);
        } finally {
            setLoginLoading(false);
        }
    };

    const isElofusion = publicSettings?.mode === Mode.ELOFUSION;

    return (
        <FusionBody className="bg-transparent">
            <LoginBackground />
            <Toaster richColors />

            <div className="flex h-full min-h-[800px] flex-col">
                <div className="flex grow">
                    <div className="flex w-full shrink-0 items-center justify-center p-4 sm:p-6 lg:w-[500px] 2xl:w-[43%] 2xl:justify-end 2xl:pr-[5%]">
                        <div className="w-full max-w-[420px] rounded-2xl bg-white p-4 shadow-lg sm:p-8">
                            <a
                                href="https://elofleet.com"
                                target="_blank"
                                rel="noreferrer"
                                className="lg:hidden"
                            >
                                <img
                                    src={elofleetLogoUrl}
                                    alt="ELOfleet logo"
                                    className="mb-4 h-9 w-auto object-contain"
                                />
                            </a>
                            <div className="mb-8 flex items-center justify-between">
                                <LanguageSelector />
                                <Button variant="outline" asChild>
                                    <a href={bookDemoUrl} target="_blank" rel="noreferrer">
                                        {t("page.login.bookDemoButton.text")}
                                    </a>
                                </Button>
                            </div>
                            <h2 className="text-2xl font-bold">
                                {t(`page.login.title.${isElofusion ? "elofusion" : "elofleet"}`)}
                            </h2>
                            <p className="text-muted-foreground mt-1 mb-6 text-sm">
                                {t("page.login.notYourWorkspace")}{" "}
                                <NavLink to={routePath.dispatcher()} className="underline">
                                    {t("page.login.findYourCompany")}
                                </NavLink>
                            </p>
                            {loginError && (
                                <Alert variant="destructive" className="mb-4">
                                    <AlertTitle>{t("page.login.failed.title")}</AlertTitle>
                                    {t("formLogin.password.error.invalid")}
                                </Alert>
                            )}
                            {ssoError && (
                                <Alert variant="destructive" className="mb-4">
                                    <AlertTitle>{t("page.login.failed.title")}</AlertTitle>
                                    {ssoError}
                                </Alert>
                            )}
                            <form
                                className="flex flex-col gap-2"
                                onSubmit={handleSubmit(handleLoginWithPassword)}
                            >
                                <Label htmlFor="email">{t("formLogin.email.label")}</Label>
                                <Input
                                    id="email"
                                    tabIndex={1}
                                    {...register("email", {
                                        required: {
                                            value: true,
                                            message: t("validation.required"),
                                        },
                                        pattern: {
                                            value: rfc5322Email,
                                            message: t("validation.invalid.email"),
                                        },
                                    })}
                                    errored={errors.email != null}
                                />
                                {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
                                <div className="mt-2 flex items-center justify-between">
                                    <Label htmlFor="password">
                                        {t("formLogin.password.label")}
                                    </Label>
                                    <a
                                        href="#"
                                        tabIndex={5}
                                        className="text-muted-foreground text-sm underline"
                                        onClick={() => setResetPasswordModalOpened(true)}
                                    >
                                        {t("formLogin.forgotPassword")}
                                    </a>
                                </div>
                                <PasswordInput
                                    id="password"
                                    tabIndex={2}
                                    {...register("password", {
                                        required: {
                                            value: true,
                                            message: t("validation.required"),
                                        },
                                    })}
                                    errored={errors.password != null}
                                />
                                {errors.password && (
                                    <ErrorText>{errors.password.message}</ErrorText>
                                )}
                                <Button
                                    tabIndex={3}
                                    className="mt-4 w-full"
                                    type="submit"
                                    disabled={loginLoading}
                                >
                                    {t("formLogin.submit.text")}
                                </Button>
                            </form>
                            <div className="mt-3">
                                {ssoAuthUrl == null ? (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipContent>
                                                {t("formLogin.ssoNotConfigured")}
                                            </TooltipContent>
                                            <TooltipTrigger
                                                className="w-full cursor-default"
                                                asChild
                                            >
                                                <span className="block w-full">
                                                    <Button
                                                        variant="outline"
                                                        className="w-full"
                                                        disabled
                                                    >
                                                        {t("formLogin.signInWithSSO")}
                                                    </Button>
                                                </span>
                                            </TooltipTrigger>
                                        </Tooltip>
                                    </TooltipProvider>
                                ) : (
                                    <Button
                                        asChild
                                        variant="outline"
                                        className="w-full"
                                        tabIndex={4}
                                    >
                                        <a href={ssoAuthUrl}>{t("formLogin.signInWithSSO")}</a>
                                    </Button>
                                )}
                            </div>
                            <NotACustomerYet className="mt-5" />
                            <Footer className="mt-6" />
                        </div>
                    </div>
                    <ShowcasePanel />
                </div>
                <Brands />
            </div>
            <ResetPasswordModal
                open={resetPasswordModalOpened}
                onOpenChange={setResetPasswordModalOpened}
            />
        </FusionBody>
    );
});
